import { useState, useRef, useEffect, useMemo } from 'react';
import './PlanetDetailedCard.css';
import { PLANET_DATA, formatScientificNotationSup, TemperatureToggle, RingDetails } from '../../utils/PlanetConstants';
import planetMissions from '../../utils/PlanetaryMissions';
import useEarthSatellites from '../../utils/UseRTreeSatelliteJSON';
import { convertRoundOrbitalSpeed, DistanceToggle, convertSiderealPeriod, calculateOrbitalPhase } from '../../utils/PlanetUtils';
import SoundWaveVisualizer from './SoundWaveVisualizer';
import PlanetCarousel from './PlanetCarousel';
import MarsExploration from '../mars_exploration/MarsExploration';

function PlanetDetailCard({
  selectedPlanet,
  missionAssets,
  onClose,
  is3DViewEnabled,
  is3DFadingOut,
  fadeOutComplete,
  isExpanded,
  setIsExpanded,
  onGlow,
  isTrackingSatellite,
  setIsTrackingSatellite,
  setIsExploringMars,
}) {
  const cardRef = useRef(null);
  const [collapsedRight, setCollapsedRight] = useState(false);
  const [currentSection, setCurrentSection] = useState(0); // 0: sound, 1: rings, 2: satellites
  const [startX, setStartX] = useState(0); // Track start position of swipe
  const [isDragging, setIsDragging] = useState(false); // Track drag state
  const [isMobile, setIsMobile] = useState(false);

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [isEnded, setIsEnded] = useState(false);

  // Memoized missions — safe to call even if missionAssets is empty or undefined
  const memoizedPlanetMissions = useMemo(() => planetMissions(missionAssets), [missionAssets]);

  // Track window width for resizing
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [isMarsGlowing, setIsMarsGlowing] = useState(false);

  
  // Early safe defaults if no selectedPlanet to avoid conditional hooks
  const planetInfo = selectedPlanet ? PLANET_DATA[selectedPlanet.name] : null;
  const showRings = planetInfo?.rings || false;
  const showMissions = planetInfo?.missions || false;
  const showSatellites = planetInfo?.trackSatellites || false;
  const { active, inactive, activeCategoryCounts, inactiveCategoryCounts } = useEarthSatellites();

  // Mars exploration
  const [showMarsExploration, setShowMarsExploration] = useState(false);

  // Memoized order of sections based on planet info
  const order = useMemo(() => {
    if (!selectedPlanet) return [];
    return [
      0,
      showRings ? 1 : null,
      showMissions ? 2 : null,
      showSatellites ? 3 : null,
    ].filter(v => v !== null);
  }, [selectedPlanet, showRings, showMissions, showSatellites]);

  // Memoized visible index of currentSection in order
  const visibleIndex = useMemo(() => {
    if (!selectedPlanet) return -1;
    return order.indexOf(currentSection);
  }, [selectedPlanet, order, currentSection]);

  // Memoized sections content JSX
  const sections = useMemo(() => {
    if (!selectedPlanet || !planetInfo) return {};
  
  const activeCategories = Object.entries(activeCategoryCounts || {}).map(([label, count]) => ({
    label,
    count: count ?? 0,
  }));

  const sortedActive = activeCategories.sort((a, b) => b.count - a.count);

  // Split into two vertical columns
  const half = Math.ceil(sortedActive.length / 2);
  const leftActive = sortedActive.slice(0, half);
  const rightActive = sortedActive.slice(half);  

  const inactiveCategories = Object.entries(inactiveCategoryCounts || {}).map(([label, count]) => ({
    label,
    count: count ?? 0,
  }));
  const sortedInactive = inactiveCategories.sort((a, b) => b.count - a.count);
  const halfInactive = Math.ceil(sortedInactive.length / 2);
  const leftInactive = sortedInactive.slice(0, halfInactive);
  const rightInactive = sortedInactive.slice(halfInactive);
  
  const formatCategoryLabel = (label) => {
    if (!label) return '';
    return label
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleTrackClick = () => {
    setIsTrackingSatellite(true);
    setIsExpanded(false);
    // console.log(!isTrackingSatellite ? 'Started tracking satellites' : 'Stopped tracking satellites');
  };

   const handleMarsExplorationClick = () => {
    if (planetInfo?.marsExploration) {
      setIsExploringMars?.(true);
      setIsExpanded(false); // collapse the card, matching handleTrackClick's pattern
    }
  };
    return {
      description: (
        <>
          <p>{planetInfo.description}</p>
          <p>
            {selectedPlanet.name !== 'Moon' && (
              <>
                <span className="satellite-label">Satellite(s):</span>{' '}
                {selectedPlanet.name === 'Earth' ? (
                  <span className="satellite-values">
                    Natural: 1 Moon<br />
                    Artificial: Active: {active}, Inactive: {inactive}
                  </span>
                ) : (
                  `${planetInfo.satellites} Moon${planetInfo.satellites !== 1 ? 's' : ''}`
                )}
              </>
            )}
          </p>
          <DistanceToggle AU={selectedPlanet.distanceFromSun} />
          <p><strong>Orbital Speed:</strong> {convertRoundOrbitalSpeed(selectedPlanet.orbital_speed)} km/s</p>
          <p><strong>Sidereal Period:</strong> {convertSiderealPeriod(planetInfo.siderealPeriod)}</p>
          <p><strong>Orbital Progress:</strong> {calculateOrbitalPhase(planetInfo.perihelionJD, planetInfo.siderealPeriod)}</p>
          <p><strong>Mass:</strong> {formatScientificNotationSup(planetInfo.mass)} kg</p>
          <TemperatureToggle kelvin={planetInfo.temperature} onGlow={onGlow} />
        </>
      ),
      rings: (
        <div className="rings-section">
          <h3>{selectedPlanet.name}'s Rings</h3>
          <RingDetails PlanetRings={planetInfo.ringDetails} isMobile={isMobile} />
        </div>
      ),
      missions: (
        <div className="missions-section">
          <div>
            <h3>
              {selectedPlanet.name === "Moon"
                ? `${selectedPlanet.name}'s Major Milestone Missions`
                : `${selectedPlanet.name}'s Missions`}
            </h3>
              {/* {planetInfo?.marsExploration && (
              <button
                className={`mars-exploration-btn ${isMarsGlowing ? 'glow' : ''}`}
                onPointerDown={() => setIsMarsGlowing(true)}
                onPointerUp={() => setIsMarsGlowing(false)}
                onPointerLeave={() => setIsMarsGlowing(false)}
                onClick={handleMarsExplorationClick}
              >
                EXPLORE MARS
                <br />
                <small>Perseverance Rover — Live Mission Data</small>
                <span className="explore-subtext">[CLICK HERE]</span>
              </button>
            )} */}
              {/* {showMarsExploration && <MarsExploration />}       */}
            <div className="missions" style={{
              overflowY: 'auto',
              maxHeight: '41vh',
              WebkitOverflowScrolling: 'touch',
            }}>
              {memoizedPlanetMissions[selectedPlanet.name]}
            </div>
          </div>
        </div>
      ),
      satellites: (        
        <div className="satellites-section">
          <div className="satellite-header">
            <h3>{selectedPlanet.name}'s Satellites</h3>
            <button className="track-btn" onClick={handleTrackClick}>
              {/* {isTrackingSatellite ? 'Stop Tracking' : 'Track Satellites'} */}
              View Satellites
            </button>
          </div>
          {planetInfo.trackSatellites ? (
            <>
              <div className= "satellite-description">We have {active + inactive} satellites orbiting Earth, both active and inactive, supporting a variety of missions from communication to science.
              </div>
              <div className="category-sat-section">
                <h4>Active Satellites: <span className="category-sat-count">{active}</span></h4>
                <div className="category-list two-column">
                  <div className="column">
                    {leftActive.map((item) => (
                      <span key={item.label}>
                        <strong>{formatCategoryLabel(item.label)}:</strong> {item.count}
                      </span>
                    ))}
                  </div>
                  <div className="column">
                    {rightActive.map((item) => (
                      <span key={item.label}>
                        <strong>{formatCategoryLabel(item.label)}:</strong> {item.count}
                      </span>
                    ))}
                  </div>
                </div>
                <hr style={{ border: 'none', height: '2px', backgroundColor: 'black', width: '100%' }} />
                <h4 style={{paddingTop: '0.6rem'}}>Inactive Satellites: <span className="category-sat-count">{inactive}</span></h4>
                <div className="category-list two-column">
                  <div className="column">
                    {leftInactive.map((item) => (
                      <span key={item.label}>
                        <strong>{formatCategoryLabel(item.label)}:</strong> {item.count}
                      </span>
                    ))}
                  </div>
                  <div className="column">
                    {rightInactive.map((item) => (
                      <span key={item.label}>
                        <strong>{formatCategoryLabel(item.label)}:</strong> {item.count}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      ),
    };
  }, [selectedPlanet, planetInfo, isMobile, memoizedPlanetMissions, onGlow]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Collapse/expand card based on screen size
  useEffect(() => {
    if (screenWidth > 800) {
      setIsMobile(false);
      // Uncomment if you want to auto-expand on large screens
      setIsExpanded(true);
    } else {
      setIsMobile(true);
      // setIsExpanded(false);
    }
  }, [screenWidth, setIsExpanded]);

  // Early return if no selectedPlanet
  if (!selectedPlanet) {
    return null;
  }

  // Sound play/pause handling
  const playSound = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      if (audio.currentTime >= audio.duration - 0.5 || audio.ended) {
        setIsEnded(false);
        audio.currentTime = 0;
      }

      audio.volume = 1.0;
      audio.play().then(() => {
        setIsPlaying(true);
        setHasPlayed(true);
        fadeOutAudio();
      }).catch(err => {
        console.error("Error playing audio:", err);
      });
    }
  };

  const fadeOutAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const fadeDuration = 2.5;
    const fadeStartTime = audio.duration - fadeDuration;

    const fadeInterval = setInterval(() => {
      if (audio.paused) {
        clearInterval(fadeInterval);
        return;
      }

      if (audio.currentTime >= fadeStartTime) {
        const remaining = audio.duration - audio.currentTime;
        const volume = Math.max(remaining / fadeDuration, 0);
        audio.volume = volume;

        if (volume === 0 || audio.currentTime >= audio.duration) {
          clearInterval(fadeInterval);
          audio.pause();
          setIsPlaying(false);
        }
      }
    }, 100);
  };

  // Swipe handlers
  const onTouchStart = (e) => {
    if (!isExpanded) return;
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const onTouchMove = (e) => {
    if (!isExpanded || !isDragging) return;
    const currentX = e.touches[0].clientX;
    const diffX = startX - currentX;

    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        handleNextSection();
      } else {
        handlePreviousSection();
      }
      setStartX(currentX);
      setIsDragging(false);
    }
  };

  const onTouchEnd = () => {
    setIsDragging(false);
  };

  const onMouseDown = (e) => {
    if (!isExpanded) return;
    setStartX(e.clientX);
    setIsDragging(true);
  };

  const onMouseMove = (e) => {
    if (!isExpanded || !isDragging) return;
    const currentX = e.clientX;
    const diffX = startX - currentX;

    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        handleNextSection();
      } else {
        handlePreviousSection();
      }
      setStartX(currentX);
      setIsDragging(false);
    }
  };

  const onMouseUp = () => {
    setIsDragging(false);
  };

  // Section navigation handlers
  const handleNextSection = () => {
    setCurrentSection((prev) => {
      const currentIndex = order.indexOf(prev);
      if (currentIndex < order.length - 1) {
        return order[currentIndex + 1];
      }
      return prev;
    });
  };

  const handlePreviousSection = () => {
    setCurrentSection((prev) => {
      const currentIndex = order.indexOf(prev);
      if (currentIndex > 0) {
        return order[currentIndex - 1];
      }
      return prev;
    });
  };

  return (
    <div
      ref={cardRef}
      className={`planet-detail-card mobile
        ${isExpanded ? 'expanded' : 'collapsed'}
        ${collapsedRight ? 'collapsed-right' : ''}
        ${is3DViewEnabled && !is3DFadingOut ? 'fade-in' : ''}
        ${is3DFadingOut ? 'faded-out' : ''}
        ${fadeOutComplete ? '' : 'hidden-after-fade'}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      <div
        className='drag-indicator-container'
        onClick={() => {
          if (screenWidth > 800) {
            setCollapsedRight(prev => !prev);
          } else {
            setIsExpanded(prev => !prev);
          }
        }}>
        <div className="drag-indicator" />
      </div>

      <button className="close-btn" onClick={onClose}>×</button>

      <div className="planet-detail-card-info">
        {currentSection === 0 && (          
          <div className="sound-container">
            <h2>{selectedPlanet.name}</h2>
            {planetInfo?.soundUrl &&(
              <>
                <button className="speaker" onClick={playSound}>
                  {isPlaying ? (
                    <svg className="sound-icon" viewBox="0 0 24 24" fill={planetInfo.color}>
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                  ) : isEnded ? (
                    <svg className="sound-icon" viewBox="0 0 24 24" fill={planetInfo.color}>
                      <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6a6 6 0 0 1-6 6c-1.66 0-3.14-.69-4.22-1.78l-1.42 1.42A7.932 7.932 0 0 0 12 21c4.42 0 8-3.58 8-8s-3.58-8-8-8z" />
                    </svg>
                  ) : (
                    <svg className="sound-icon" viewBox="0 0 24 24" fill={planetInfo.color}>
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
                {(isPlaying || (hasPlayed && !isEnded)) && (
                  <SoundWaveVisualizer
                    audioRef={audioRef}
                    isPlaying={isPlaying}
                    isEnded={isEnded}
                    color={planetInfo.color}
                  />
                )}
              </>
            )}
          </div>
        )}
        <PlanetCarousel
          currentSection={currentSection}
          showRings={showRings}
          showMissions={showMissions}
          showSatellites={showSatellites}
          sections={sections}
        />
      </div>
      <div className="carousel-indicator">
        {order.map((sectionIndex, i) => (
          <div
            key={sectionIndex}
            className={`dot ${visibleIndex === i ? 'filled' : ''}`}
          />
        ))}
      </div>
      <audio ref={audioRef}>
        <source src={planetInfo.soundUrl} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
}

export default PlanetDetailCard;
